import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { trackEvent } from '../analytics';
import { API_BASE } from '../api';
import {
  generatePostShareImage,
  generateProfileShareImage,
  extractProminentColors,
  downloadDataURL,
} from '../utils/generateShareImage';

var Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

var Modal = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 320px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.25rem;
  position: relative;
`;

var CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #999;
  font-size: 1.25rem;
  line-height: 1;
`;

var Title = styled.h3`
  margin: 0 0 0.6rem;
  font-size: 1rem;
  text-align: center;
`;

var OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

var OptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.7rem 0.75rem;
  background: white;
  border: 1px solid #eee;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  text-align: left;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

var OptionIcon = styled.span`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

var FormatToggle = styled.div`
  display: flex;
  background: #f0f0f0;
  border-radius: 10px;
  padding: 3px;
  width: 100%;
`;

var FormatOption = styled.button`
  flex: 1;
  padding: 0.55rem 0;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: ${function (p) {
    return p.active ? 'white' : 'transparent';
  }};
  color: ${function (p) {
    return p.active ? '#111' : '#888';
  }};
  box-shadow: ${function (p) {
    return p.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none';
  }};
`;

var PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
`;

var PreviewWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
`;

var PreviewImage = styled.img`
  max-width: 100%;
  max-height: 30vh;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
`;

var SaveButton = styled.button`
  width: 100%;
  padding: 0.6rem;
  background: black;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #222;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

var BackButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #333;
  }
`;

var CopyLinkRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.6rem;
  background: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #555;
  cursor: pointer;

  &:hover {
    background: #eee;
  }
`;

var Instructions = styled.div`
  font-size: 0.72rem;
  color: #999;
  text-align: center;
  line-height: 1.5;
`;

var ColorSwatches = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
`;

var Swatch = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${function (p) {
      return p.selected ? '#111' : 'transparent';
    }};
  outline: ${function (p) {
    return p.selected ? '2px solid white' : 'none';
  }};
  cursor: pointer;
  padding: 0;
  background: ${function (p) {
    return p.color;
  }};
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
`;

function ShareModal(props) {
  // Append ?ref=<userId> to shared URLs for referral attribution
  var rawUrl = props.url;
  var url = rawUrl;
  try {
    var authData = JSON.parse(localStorage.getItem('authData') || '{}');
    if (authData.user && authData.user.id) {
      var sep = rawUrl.indexOf('?') >= 0 ? '&' : '?';
      url = rawUrl + sep + 'ref=' + authData.user.id;
    }
  } catch (e) {}
  var onClose = props.onClose;
  var shareType = props.shareType || 'unknown';
  var shareText = props.text || 'Join me on HeavyRotation and discover new music!';
  var imageData = props.imageData;

  var _copied = useState(false);
  var copied = _copied[0];
  var setCopied = _copied[1];

  var _igMode = useState(false);
  var igMode = _igMode[0];
  var setIgMode = _igMode[1];

  var _igFormat = useState('post'); // "post" or "story"
  var igFormat = _igFormat[0];
  var setIgFormat = _igFormat[1];

  var _igPreview = useState(null);
  var igPreview = _igPreview[0];
  var setIgPreview = _igPreview[1];

  var _generating = useState(false);
  var generating = _generating[0];
  var setGenerating = _generating[1];

  var _bgColor = useState(shareType === 'profile' ? 'collage' : '#f5f5f5');
  var bgColor = _bgColor[0];
  var setBgColor = _bgColor[1];

  var _compact = useState(false);
  var compact = _compact[0];
  var setCompact = _compact[1];

  var _colors = useState([]);
  var colors = _colors[0];
  var setColors = _colors[1];

  // Extract prominent colors when entering IG mode
  useEffect(
    function () {
      if (!igMode || !imageData) return;
      // For posts: use artwork. For profiles: use profile pic.
      var artSrc;
      if (shareType === 'post') {
        artSrc = imageData.artworkURL;
      } else {
        artSrc = imageData.profilePicURL;
      }
      if (!artSrc) return;
      if (artSrc.startsWith('/')) {
        artSrc = API_BASE + artSrc;
      }
      extractProminentColors(artSrc).then(function (result) {
        setColors(result);
      });
    },
    [igMode, imageData, shareType]
  );

  // Generate preview whenever format, bgColor, or compact changes in IG mode
  useEffect(
    function () {
      if (!igMode || !imageData) return;
      setGenerating(true);
      var isCollage = bgColor === 'collage';
      var actualBg = isCollage ? '#1a1a1a' : bgColor;
      var generateFn;
      if (shareType === 'post') {
        var opts = Object.assign({}, imageData, { compact: compact });
        generateFn = generatePostShareImage(opts, igFormat, actualBg);
      } else {
        var profileOpts = Object.assign({}, imageData, { useCollage: isCollage });
        generateFn = generateProfileShareImage(profileOpts, igFormat, actualBg);
      }
      generateFn
        .then(function (dataURL) {
          setIgPreview(dataURL);
          setGenerating(false);
        })
        .catch(function () {
          setGenerating(false);
        });
    },
    [igMode, igFormat, bgColor, compact, imageData, shareType]
  );

  function handleCopyLink() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(function () {
        trackEvent('share_completed', { type: shareType, method: 'copy_link', url: url });
        setCopied(true);
        setTimeout(function () {
          setCopied(false);
          onClose();
        }, 1200);
      });
    }
  }

  function handleFacebook() {
    trackEvent('share_completed', { type: shareType, method: 'facebook', url: url });
    window.open(
      'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url),
      '_blank',
      'noopener,noreferrer,width=600,height=400'
    );
    onClose();
  }

  function handleTwitter() {
    trackEvent('share_completed', { type: shareType, method: 'twitter', url: url });
    window.open(
      'https://twitter.com/intent/tweet?url=' +
        encodeURIComponent(url) +
        '&text=' +
        encodeURIComponent(shareText),
      '_blank',
      'noopener,noreferrer,width=600,height=400'
    );
    onClose();
  }

  function handleWhatsApp() {
    trackEvent('share_completed', { type: shareType, method: 'whatsapp', url: url });
    window.open(
      'https://wa.me/?text=' + encodeURIComponent(shareText + ' ' + url),
      '_blank',
      'noopener,noreferrer'
    );
    onClose();
  }

  function handleIMessage() {
    trackEvent('share_completed', { type: shareType, method: 'imessage', url: url });
    var smsText = 'Join me on HeavyRotation and discover new music! ' + url;
    window.location.href = 'sms:?&body=' + encodeURIComponent(smsText);
    onClose();
  }

  var _igCopied = useState(false);
  var igCopied = _igCopied[0];
  var setIgCopied = _igCopied[1];

  function handleInstagram() {
    setIgMode(true);
  }

  function handleIgCopyLink() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(function () {
        trackEvent('share_completed', { type: shareType, method: 'instagram_copy_link', url: url });
        setIgCopied(true);
        setTimeout(function () {
          setIgCopied(false);
        }, 2000);
      });
    }
  }

  function handleSave() {
    if (!igPreview) return;
    trackEvent('share_completed', { type: shareType, method: 'instagram_' + igFormat, url: url });
    downloadDataURL(igPreview, 'heavyrotation-' + igFormat + '.png');
  }

  // Instagram preview screen
  if (igMode) {
    return (
      <Overlay onClick={onClose}>
        <Modal
          onClick={function (e) {
            e.stopPropagation();
          }}
        >
          <CloseButton onClick={onClose}>&times;</CloseButton>
          <Title>Instagram</Title>
          <PreviewContainer>
            <Instructions>
              Save the image, then open Instagram to Post or add to your Story.
            </Instructions>
            <FormatToggle>
              <FormatOption
                active={igFormat === 'post'}
                onClick={function () {
                  setIgFormat('post');
                }}
              >
                Post
              </FormatOption>
              <FormatOption
                active={igFormat === 'story'}
                onClick={function () {
                  setIgFormat('story');
                }}
              >
                Story
              </FormatOption>
            </FormatToggle>
            {shareType === 'post' && (
              <FormatToggle>
                <FormatOption
                  active={!compact}
                  onClick={function () {
                    setCompact(false);
                  }}
                >
                  Full Post
                </FormatOption>
                <FormatOption
                  active={compact}
                  onClick={function () {
                    setCompact(true);
                  }}
                >
                  Art Only
                </FormatOption>
              </FormatToggle>
            )}
            {(colors.length > 0 ||
              (shareType === 'profile' && imageData?.recentArtwork?.length > 0)) && (
              <ColorSwatches>
                {shareType === 'profile' && imageData?.recentArtwork?.length > 0 && (
                  <Swatch
                    color="transparent"
                    selected={bgColor === 'collage'}
                    onClick={function () {
                      setBgColor('collage');
                    }}
                    style={{
                      background: '#222',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Collage"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    >
                      <rect x="1" y="1" width="6" height="6" rx="1" />
                      <rect x="9" y="1" width="6" height="6" rx="1" />
                      <rect x="1" y="9" width="6" height="6" rx="1" />
                      <rect x="9" y="9" width="6" height="6" rx="1" />
                    </svg>
                  </Swatch>
                )}
                {colors.map(function (c, i) {
                  return (
                    <Swatch
                      key={i}
                      color={c}
                      selected={bgColor === c}
                      onClick={function () {
                        setBgColor(c);
                      }}
                    />
                  );
                })}
              </ColorSwatches>
            )}
            <PreviewWrapper>
              {generating ? (
                <span style={{ color: '#999', fontSize: '0.85rem' }}>Generating...</span>
              ) : igPreview ? (
                <PreviewImage src={igPreview} alt="Share preview" />
              ) : null}
            </PreviewWrapper>
            <SaveButton onClick={handleSave} disabled={generating || !igPreview}>
              Save Image
            </SaveButton>
            <CopyLinkRow onClick={handleIgCopyLink}>
              {igCopied ? (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  Copy link to paste in caption
                </>
              )}
            </CopyLinkRow>
            <BackButton
              onClick={function () {
                setIgMode(false);
                setIgPreview(null);
                setBgColor(shareType === 'profile' ? 'collage' : '#f5f5f5');
                setCompact(false);
                setColors([]);
              }}
            >
              &larr; Back
            </BackButton>
          </PreviewContainer>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={onClose}>
      <Modal
        onClick={function (e) {
          e.stopPropagation();
        }}
      >
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <Title>{props.title || 'Share'}</Title>
        <OptionsList>
          <OptionButton onClick={handleCopyLink}>
            <OptionIcon>
              {copied ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              )}
            </OptionIcon>
            {copied ? 'Copied!' : 'Copy Link'}
          </OptionButton>
          {imageData && (
            <OptionButton onClick={handleInstagram}>
              <OptionIcon>
                <svg viewBox="0 0 24 24" fill="none">
                  <defs>
                    <linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FD5" />
                      <stop offset="50%" stopColor="#FF543E" />
                      <stop offset="100%" stopColor="#C837AB" />
                    </linearGradient>
                  </defs>
                  <rect
                    x="2"
                    y="2"
                    width="20"
                    height="20"
                    rx="5"
                    stroke="url(#ig)"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="12" r="5" stroke="url(#ig)" strokeWidth="2" />
                  <circle cx="17.5" cy="6.5" r="1.5" fill="url(#ig)" />
                </svg>
              </OptionIcon>
              Instagram
            </OptionButton>
          )}
          <OptionButton onClick={handleFacebook}>
            <OptionIcon>
              <svg viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </OptionIcon>
            Facebook
          </OptionButton>
          <OptionButton onClick={handleTwitter}>
            <OptionIcon>
              <svg viewBox="0 0 24 24" fill="#000">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </OptionIcon>
            X / Twitter
          </OptionButton>
          <OptionButton onClick={handleWhatsApp}>
            <OptionIcon>
              <svg viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </OptionIcon>
            WhatsApp
          </OptionButton>
          <OptionButton onClick={handleIMessage}>
            <OptionIcon>
              <svg viewBox="0 0 24 24" fill="#34C759">
                <path d="M12 2C6.477 2 2 5.813 2 10.5c0 2.61 1.432 4.942 3.67 6.46-.174.91-.58 2.107-1.346 3.29-.14.216-.014.5.24.5.756 0 2.033-.548 3.39-1.482A11.18 11.18 0 0012 19.99c5.523 0 10-3.806 10-8.49S17.523 2 12 2z" />
              </svg>
            </OptionIcon>
            Text Message
          </OptionButton>
        </OptionsList>
      </Modal>
    </Overlay>
  );
}

export default ShareModal;
